import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { AuthInitializerService } from '../services/auth-initializer.service';
import { Observable, of } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Guard that protects routes requiring authentication
 * Redirects to login if not authenticated
 */
export const authGuard: CanActivateFn = (route, state) => {
  const showDebugInfo = environment.showDebugInfo;
  
  if (showDebugInfo) {
    console.log('Auth guard activated for route:', state.url);
  }
  
  const router = inject(Router);
  const authService = inject(AuthService);
  const authInitializer = inject(AuthInitializerService);
  
  // Check if auth is ready
  if (!authInitializer.isAuthReady()) {
    if (showDebugInfo) {
      console.log('Auth guard: Auth not ready, waiting for initialization');
    }
    // Wait for auth to be ready
    return of(authInitializer.initializeAuth()).pipe(
      switchMap(() => checkAuthState(authService, router, state.url, showDebugInfo))
    );
  }
  
  return checkAuthState(authService, router, state.url, showDebugInfo);
};

/**
 * Guard that protects public routes (like login)
 * Redirects to dashboard if already authenticated
 */
export const publicGuard: CanActivateFn = (route, state) => {
  const showDebugInfo = environment.showDebugInfo;
  
  if (showDebugInfo) {
    console.log('Public guard activated for route:', state.url);
  }
  
  const router = inject(Router);
  const authService = inject(AuthService);
  const authInitializer = inject(AuthInitializerService);
  
  // Check if auth is ready
  if (!authInitializer.isAuthReady()) {
    if (showDebugInfo) {
      console.log('Public guard: Auth not ready, waiting for initialization');
    }
    // Wait for auth to be ready
    return of(authInitializer.initializeAuth()).pipe(
      switchMap(() => checkPublicAccess(authService, router, showDebugInfo))
    );
  }
  
  return checkPublicAccess(authService, router, showDebugInfo);
};

/**
 * Helper function to check auth state
 */
function checkAuthState(authService: AuthService, router: Router, url: string, showDebugInfo: boolean): Observable<boolean> {
  return authService.checkAuthenticated().pipe(
    take(1),
    map(isAuthenticated => {
      if (showDebugInfo) {
        console.log('Auth guard check result:', isAuthenticated);
      }
      
      if (isAuthenticated) {
        if (showDebugInfo) {
          console.log('Auth guard: Access granted - user is authenticated');
        }
        return true;
      }
      
      if (showDebugInfo) {
        console.log('Auth guard: Access denied - redirecting to login');
      }
      router.navigate(['/login'], { queryParams: { returnUrl: url } });
      return false;
    })
  );
}

/**
 * Helper function to check public access
 */
function checkPublicAccess(authService: AuthService, router: Router, showDebugInfo: boolean): Observable<boolean> {
  return authService.checkAuthenticated().pipe(
    take(1),
    map(isAuthenticated => {
      if (showDebugInfo) {
        console.log('Public guard check result:', isAuthenticated);
      }
      
      if (isAuthenticated) {
        if (showDebugInfo) {
          console.log('Public guard: Access denied - redirecting to dashboard');
        }
        router.navigate(['/dashboard']);
        return false;
      }
      
      if (showDebugInfo) {
        console.log('Public guard: Access granted - no authenticated user');
      }
      return true;
    })
  );
} 